import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { APP_URL } from '../lib/appUrl';
import { ArrowRightIcon, CalendarBlankIcon, MapPinIcon, TicketIcon } from '@phosphor-icons/react';

const EVENTS_FEED = 'https://api.eventsportal.live/public/events';
const APP_STORE_URL = 'https://apps.apple.com/app/portal-event-tickets/id6766731094';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.bankrollcollective.portalapp';

const fmtDate = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();
    } catch { return ''; }
};
const fmtTime = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
};

function EventCard({ e }) {
    const sold = (e.total_tickets ?? 0) > 0 && (e.tickets_sold ?? 0) >= e.total_tickets;
    const eventUrl = `${APP_URL}/event/${e.id}`;
    return (
        <a
            href={eventUrl}
            data-testid={`event-card-${e.id}`}
            className="group flex flex-col bg-[#0A0A0B] border border-white/10 hover:border-neon transition-colors overflow-hidden"
        >
            <div className="relative aspect-[4/5] bg-[#121214] overflow-hidden">
                {e.image_url ? (
                    <img
                        src={e.image_url}
                        alt={e.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover saturate-110 group-hover:scale-105 transition-transform duration-700"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center font-display text-6xl text-white/10 uppercase">
                        Portal
                    </div>
                )}
                {/* Top tag */}
                <div className="absolute top-3 left-3 flex items-center gap-2">
                    {e.category && (
                        <span className="bg-neon text-black px-2 py-1 text-[9px] uppercase tracking-[0.25em] font-mono font-bold">
                            {e.category}
                        </span>
                    )}
                    {sold && (
                        <span className="bg-red-500 text-white px-2 py-1 text-[9px] uppercase tracking-[0.25em] font-mono font-bold">
                            Sold out
                        </span>
                    )}
                </div>
                {/* Date badge */}
                <div className="absolute bottom-3 left-3 bg-black/80 backdrop-blur px-3 py-2 border border-white/15">
                    <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-neon leading-none mb-1">
                        {fmtDate(e.date)}
                    </p>
                    <p className="font-display text-base uppercase leading-none">{fmtTime(e.date)}</p>
                </div>
            </div>
            <div className="p-5 flex flex-col gap-3 flex-1">
                <h3 className="font-display text-2xl uppercase leading-tight tracking-tight group-hover:text-neon transition-colors line-clamp-2">
                    {e.title || 'Untitled'}
                </h3>
                {(e.venue || e.city) && (
                    <p className="text-xs text-white/60 flex items-center gap-1.5 line-clamp-1">
                        <MapPinIcon size={12} weight="bold" className="text-neon flex-shrink-0" />
                        {[e.venue, e.city].filter(Boolean).join(' / ') || 'Location TBA'}
                    </p>
                )}
                <div className="mt-auto pt-3 border-t border-white/10 flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                        {e.price ? `From $${Number(e.price).toFixed(0)}` : 'Free'}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neon flex items-center gap-1">
                        Get in <ArrowRightIcon size={11} weight="bold" />
                    </span>
                </div>
            </div>
        </a>
    );
}

function EventsGrid() {
    const [events, setEvents] = useState(null);
    const [error, setError] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                // Cache-bust so a stale CDN copy doesn't hide a just-pulled event
                const res = await fetch(`${EVENTS_FEED}?t=${Date.now()}`, {
                    headers: { Accept: 'application/json' },
                    cache: 'no-store',
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (cancelled) return;
                const list = Array.isArray(data?.events) ? data.events : [];
                // Trust ONLY public, live events. Past events drop off automatically.
                // - status: 'published' or 'sold_out' (sold_out kept as social proof)
                // - off_grid: false
                // - date is now or future (with 6h grace so an event still happening tonight stays up)
                const now = Date.now();
                const SIX_HOURS = 1000 * 60 * 60 * 6;
                const filtered = list
                    .filter((e) => ['published', 'sold_out'].includes(e?.status) && !e?.off_grid)
                    .filter((e) => !e?.date || new Date(e.date).getTime() >= now - SIX_HOURS)
                    .sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
                setEvents(filtered);
                setUpdatedAt(Date.now());
                setError(null);
            } catch (err) {
                if (!cancelled) setError(err.message || 'Could not load events');
            }
        };

        // Initial load + recurring refresh.
        // Catches: deletes, unpublishes (status → draft/cancelled), off_grid toggles,
        // sold_out flips, and newly-published events.
        load();
        const interval = setInterval(load, 60_000); // every 60s

        // Refetch the moment the tab regains focus (laptop wake, tab switch).
        const onVisible = () => { if (document.visibilityState === 'visible') load(); };
        document.addEventListener('visibilitychange', onVisible);

        // Local "expiry" sweep — every minute, drop events whose date passed
        // even if Base 44 hasn't been re-fetched yet.
        const sweep = setInterval(() => {
            setEvents((prev) => {
                if (!prev) return prev;
                const cutoff = Date.now() - 1000 * 60 * 60 * 6;
                const next = prev.filter((e) => !e?.date || new Date(e.date).getTime() >= cutoff);
                return next.length === prev.length ? prev : next;
            });
        }, 60_000);

        return () => {
            cancelled = true;
            clearInterval(interval);
            clearInterval(sweep);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    return (
        <section className="border-b border-white/10" data-testid="events-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon mb-3 flex items-center gap-3">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-neon animate-pulse" />
                            Live · upcoming
                            {updatedAt && (
                                <span className="text-white/30 normal-case tracking-[0.2em]" data-testid="events-updated">
                                    · refreshed {new Date(updatedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                </span>
                            )}
                        </p>
                        <h2 className="font-display uppercase text-5xl sm:text-6xl tracking-tight leading-[0.9]">
                            On the <span className="text-neon">floor.</span>
                        </h2>
                    </div>
                    <a href={APP_URL} className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] font-mono text-white/70 hover:text-neon transition-colors" data-testid="see-all-link">
                        See all
                        <ArrowRightIcon size={12} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                    </a>
                </div>

                {events === null && !error && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="events-loading">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="bg-[#0A0A0B] border border-white/10">
                                <div className="aspect-[4/5] bg-[#121214] animate-pulse" />
                                <div className="p-5 space-y-3">
                                    <div className="h-6 bg-white/10 animate-pulse rounded" />
                                    <div className="h-3 bg-white/5 animate-pulse rounded w-2/3" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {error && (
                    <div className="border border-white/10 bg-[#0A0A0B] p-10 text-center" data-testid="events-error">
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mb-2">// Couldn't load events</p>
                        <p className="text-white/70 text-sm">Try again in a minute, or head straight in.</p>
                        <a href={APP_URL} className="inline-block mt-6">
                            <Button className="bg-neon text-black hover:bg-[#E8FF6B] rounded-none uppercase tracking-[0.2em] text-xs font-bold px-6 py-5">
                                Open Portal
                            </Button>
                        </a>
                    </div>
                )}

                {events && events.length === 0 && !error && (
                    <div className="border border-white/10 bg-[#0A0A0B] p-12 text-center" data-testid="events-empty">
                        <p className="font-mono text-xs uppercase tracking-[0.3em] text-neon mb-3">// 00 upcoming</p>
                        <h3 className="font-display text-3xl uppercase mb-3">The room is quiet.</h3>
                        <p className="text-white/60 text-sm max-w-md mx-auto leading-relaxed mb-6">
                            No public events on Portal right now. Host your own — we'll have the door ready.
                        </p>
                        <a href={APP_URL}>
                            <Button className="bg-neon text-black hover:bg-[#E8FF6B] rounded-none uppercase tracking-[0.2em] text-xs font-bold px-8 py-6">
                                <TicketIcon size={14} weight="bold" className="mr-2" />
                                Host an event
                            </Button>
                        </a>
                    </div>
                )}

                {events && events.length > 0 && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="events-grid">
                        {events.map((e) => <EventCard key={e.id} e={e} />)}
                    </div>
                )}
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <div className="min-h-screen grain scanlines">
            <Navbar />

            {/* HERO — UNCHANGED */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div className="absolute inset-0 -z-0">
                    <img
                        src="https://images.unsplash.com/photo-1591833417792-0d3d96df6159?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
                        alt="Crowd on a dark dance floor under stage lights at a nightclub event"
                        loading="eager"
                        decoding="async"
                        className="w-full h-full object-cover opacity-50 saturate-50"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050506] via-[#050506]/70 to-[#050506]/20" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-20 lg:pt-24 lg:pb-32">
                    <div className="flex items-center gap-4 mb-12 fade-up" data-testid="hero-overline">
                        <span className="inline-block w-2 h-2 rounded-full bg-neon animate-pulse" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon">
                            The ticketing app for nights worth remembering
                        </span>
                    </div>
                    <h1
                        className="font-display uppercase tracking-tight leading-[0.82] text-white mb-8 fade-up"
                        data-testid="hero-title"
                    >
                        <span className="block text-7xl sm:text-9xl lg:text-[12rem] xl:text-[14rem]">Tonight</span>
                        <span className="block text-7xl sm:text-9xl lg:text-[12rem] xl:text-[14rem] text-neon neon-glow">
                            isn't
                        </span>
                        <span className="block text-7xl sm:text-9xl lg:text-[12rem] xl:text-[14rem]">over.</span>
                    </h1>
                    <p className="text-base sm:text-xl text-white/70 max-w-2xl leading-relaxed mb-10 fade-up">
                        Inclusive ticketing for nightlife — warehouse parties, drag shows, after-hours, brunches,
                        and every room in between.
                    </p>
                    <div className="flex flex-wrap items-center gap-3 fade-up">
                        <a href={APP_URL}>
                            <Button
                                className="bg-neon text-black hover:bg-[#E8FF6B] rounded-none px-10 py-7 text-sm uppercase tracking-[0.2em] font-bold"
                                data-testid="hero-getin-btn"
                            >
                                Get in <ArrowRightIcon size={16} weight="bold" className="ml-3" />
                            </Button>
                        </a>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 mt-8 fade-up" data-testid="store-badges">
                        <a
                            href={APP_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Download Portal on the App Store"
                            data-testid="badge-app-store"
                            className="hover:opacity-80 transition-opacity"
                        >
                            <img src="/badge-app-store.svg" alt="Download on the App Store" className="h-12 w-auto" />
                        </a>
                        <a
                            href={PLAY_STORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Get Portal on Google Play"
                            data-testid="badge-google-play"
                            className="hover:opacity-80 transition-opacity"
                        >
                            <img src="/badge-google-play.png" alt="Get it on Google Play" className="h-[60px] w-auto -my-[6px]" />
                        </a>
                    </div>
                </div>

                {/* Marquee */}
                <div className="relative border-t border-b border-neon/30 bg-neon text-black overflow-hidden">
                    <div className="marquee-track flex whitespace-nowrap py-4 font-display uppercase text-2xl sm:text-3xl tracking-tight">
                        {Array.from({ length: 2 }).map((_, k) => (
                            <div key={k} className="flex items-center shrink-0">
                                {[
                                    'Multi-tier tickets',
                                    'Stripe checkout',
                                    'Off-grid events',
                                    'Auto-rolling tiers',
                                    'Drag · Warehouse · After hours',
                                    'Direct organizer payouts',
                                    'Lineup tools',
                                ].map((t, i) => (
                                    <span key={`${k}-${i}`} className="flex items-center px-8">
                                        <span>{t}</span>
                                        <span className="mx-8 text-black/50">✦</span>
                                    </span>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* EVENTS FEED */}
            <EventsGrid />

            <footer className="border-t border-white/10 py-10 text-center text-[10px] text-white/40 font-mono uppercase tracking-[0.3em]">
                <a href="/about" className="hover:text-neon transition-colors mr-6" data-testid="footer-about-link">About Portal</a>
                <span className="opacity-50">© Portal 2026 // Built for nights worth losing</span>
            </footer>
        </div>
    );
}
